import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Check, Heart, ExternalLink, Sparkles, Lock } from "lucide-react";

const PRESETS = [5, 25, 50, 100, 250, 500];

const TIER_META = {
  "truth-spark":           { color: "#B89469" },
  "truth-carrier":         { color: "#C9A055" },
  "truth-builder":         { color: "#D4A547" },
  "truth-keeper":          { color: "#E0B872" },
  "truth-ambassador":      { color: "#A07A4A" },
  "truth-legacy-partner":  { color: "#8A6F3C" },
};

export default function Support() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [tiers, setTiers] = useState([]);
  const [progress, setProgress] = useState(null);
  const [supporters, setSupporters] = useState([]);
  const [sponsorables, setSponsorables] = useState([]);
  const [amount, setAmount] = useState(50);
  const [customMode, setCustomMode] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [showOnWall, setShowOnWall] = useState(true);
  const [sponsorMode, setSponsorMode] = useState(false);
  const [sponsorArtwork, setSponsorArtwork] = useState(null);
  const [sponsorCity, setSponsorCity] = useState("");
  const [busy, setBusy] = useState(false);
  const [thanksOpen, setThanksOpen] = useState(false);
  const [thanksData, setThanksData] = useState(null);

  // Load everything
  const reload = async () => {
    try {
      const [t, p, s, a] = await Promise.all([
        http.get("/support/tiers"),
        http.get("/support/progress"),
        http.get("/support/supporters", { params: { limit: 60 } }),
        http.get("/support/sponsor-artworks"),
      ]);
      setTiers(t.data || []);
      setProgress(p.data);
      setSupporters(s.data || []);
      setSponsorables(a.data || []);
    } catch (e) {
      console.error("load failed", e);
    }
  };

  useEffect(() => { reload(); }, []);

  // Post-checkout thanks: if a session_id is on the URL, poll for status
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await http.get(`/support/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setThanksData(data.supporter);
          setThanksOpen(true);
          reload(); // refresh progress + wall
        } else {
          setTimeout(poll, 2000);
        }
      } catch (e) {
        console.warn(e);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const currentTier = useMemo(() => {
    if (!tiers.length) return null;
    let chosen = tiers[0];
    for (const t of tiers) if (Number(amount) >= t.min_amount) chosen = t;
    return chosen;
  }, [amount, tiers]);

  const submit = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Add your name and email to continue.");
      return;
    }
    const amt = Number(amount);
    if (!amt || amt < 1) { toast.error("Enter a valid amount."); return; }
    if (sponsorMode) {
      if (!sponsorArtwork) { toast.error("Pick a piece to sponsor."); return; }
      if (amt < 50) { toast.error("Sponsor-A-Truth minimum is $50."); return; }
    }
    setBusy(true);
    try {
      const { data } = await http.post("/support/checkout", {
        amount: amt,
        email: email.trim(),
        name: name.trim(),
        message: message.trim() || null,
        show_on_wall: showOnWall,
        origin_url: window.location.origin,
        is_sponsor_a_truth: sponsorMode,
        sponsor_artwork_id: sponsorMode ? sponsorArtwork?.id : null,
        sponsor_city: sponsorMode ? sponsorCity.trim() : null,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout.");
    } finally {
      setBusy(false);
    }
  };

  const raisedDollars = progress ? (progress.raised_cents / 100) : 0;
  const goalDollars = progress ? (progress.mission.goal_cents / 100) : 5000;
  const pct = progress?.percent || 0;

  return (
    <Layout>
      {/* ====== HERO ====== */}
      <section className="relative bg-[#050505] pt-24 pb-16 sm:pt-32 sm:pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
             style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, #C9A055 0%, transparent 60%)' }} />
        <div className="max-w-3xl mx-auto px-6 text-center relative">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F4F1EA] leading-[1.05] mb-7"
              data-testid="support-headline">
            Help Us Spread<br />More Truth
          </h1>
          <div className="space-y-4 font-body text-[15px] leading-[1.8] text-[#B5B0A7]">
            <p>Every piece of artwork begins with a simple idea.</p>
            <p>A reminder. A perspective shift. A conversation that might help someone on a day they need it most.</p>
            <p>
              When you support Simple Truths Club, you&rsquo;re helping us create new artwork,
              tell more stories, build the Truth Registry, expand the Mobile QR Gallery,
              and place meaningful reminders where people need them most.
            </p>
            <p className="text-[#F4F1EA] italic">
              This isn&rsquo;t about selling prints. It&rsquo;s about creating impact.
            </p>
          </div>
        </div>
      </section>

      {/* ====== LIVE PROGRESS ====== */}
      {progress?.mission?.active && (
        <section className="bg-[#0a0a0a] border-y border-white/10 py-12 sm:py-16" data-testid="support-progress-section">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-7">
              <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-3">Current Mission</div>
              <div className="font-display text-2xl sm:text-3xl text-[#F4F1EA] mb-3" data-testid="mission-title">
                {progress.mission.title}
              </div>
              <div className="font-body text-sm text-[#8a857d] max-w-xl mx-auto">
                {progress.mission.description}
              </div>
            </div>

            <div className="mb-3 flex justify-between items-baseline" data-testid="progress-stats">
              <div className="font-display text-lg text-[#F4F1EA]">
                ${raisedDollars.toLocaleString(undefined, { maximumFractionDigits: 0 })} <span className="text-[#8a857d] font-ui text-xs">raised</span>
              </div>
              <div className="font-ui text-[11px] tracking-[0.22em] text-[#C9A055]">
                {pct.toFixed(0)}%
              </div>
              <div className="font-display text-lg text-[#8a857d]">
                ${goalDollars.toLocaleString(undefined, { maximumFractionDigits: 0 })} <span className="font-ui text-xs">goal</span>
              </div>
            </div>

            <div className="relative h-3 bg-[#1a1a1a] border border-white/5 overflow-hidden mb-7" data-testid="progress-bar-track">
              <div
                className="h-full transition-all duration-700"
                style={{
                  width: `${Math.max(2, pct)}%`,
                  background: "linear-gradient(90deg, #8A6F3C 0%, #C9A055 50%, #E5C77B 100%)",
                  boxShadow: "0 0 24px rgba(201,160,85,0.4)",
                }}
                data-testid="progress-bar-fill"
              />
            </div>

            <div className="text-center font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mb-4">
              When We Reach This Goal
            </div>
            <ul className="grid sm:grid-cols-2 gap-2 max-w-md mx-auto">
              {(progress.mission.outcomes || []).map((o, i) => (
                <li key={i} className="flex items-start gap-2 font-body text-[13px] text-[#B5B0A7]">
                  <Check size={14} className="text-[#C9A055] mt-0.5 shrink-0" /> {o}
                </li>
              ))}
            </ul>
            {progress.supporter_count > 0 && (
              <div className="text-center mt-7 font-ui text-[11px] tracking-[0.22em] uppercase text-[#8a857d]">
                {progress.supporter_count} {progress.supporter_count === 1 ? "supporter" : "supporters"} so far
              </div>
            )}
          </div>
        </section>
      )}

      {/* ====== WHERE YOUR SUPPORT GOES ====== */}
      <section className="bg-[#050505] py-16 sm:py-20" data-testid="support-where">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-4">
            Where Your Support Goes
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-[#F4F1EA] mb-4">Your support helps fund</h2>
          <div className="w-12 h-px bg-[#C9A055] mx-auto mb-10" />
          <ul className="grid sm:grid-cols-2 gap-3 text-left max-w-xl mx-auto">
            {[
              "New artwork and stories",
              "Mobile QR Gallery expansion",
              "Truth Registry development",
              "Community art displays",
              "Website improvements",
              "Free inspirational content",
              "Future events and pop-up galleries",
            ].map((s) => (
              <li key={s} className="flex items-start gap-3 font-body text-[14px] text-[#B5B0A7]">
                <Check size={16} className="text-[#C9A055] mt-1 shrink-0" /> {s}
              </li>
            ))}
          </ul>
          <p className="mt-10 font-body italic text-[#F4F1EA]">
            Every dollar helps us put more truth into the world.
          </p>
        </div>
      </section>

      {/* ====== TIERS ====== */}
      <section className="bg-[#0a0a0a] py-16 sm:py-20" data-testid="support-tiers">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-4">
              Mission Supporter Levels
            </div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#F4F1EA]">Choose your tier</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tiers.map((t) => {
              const tone = TIER_META[t.slug]?.color || "#C9A055";
              const isActive = currentTier?.slug === t.slug;
              return (
                <button
                  type="button"
                  key={t.slug}
                  onClick={() => { setAmount(t.min_amount); setCustomMode(false); document.getElementById("support-form")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                  data-testid={`tier-card-${t.slug}`}
                  className={`text-left border bg-[#0a0a0a] p-6 transition-all hover:border-[${tone}] ${isActive ? "ring-1 ring-[#C9A055]" : "border-white/10"}`}
                  style={{ borderColor: isActive ? tone : undefined }}
                >
                  <div className="flex items-baseline justify-between mb-3">
                    <div className="font-display text-xl text-[#F4F1EA]">{t.name}</div>
                    <div className="font-display text-2xl" style={{ color: tone }}>${t.min_amount}+</div>
                  </div>
                  <ul className="space-y-1.5 mt-4">
                    {t.rewards.map((rwd, i) => (
                      <li key={i} className="flex items-start gap-2 font-body text-[13px] text-[#B5B0A7]">
                        <Check size={12} className="mt-1 shrink-0" style={{ color: tone }} />
                        {rwd}
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ====== SPONSOR A TRUTH ====== */}
      {sponsorables.length > 0 && (
        <section className="bg-[#050505] py-16 sm:py-20 border-y border-white/5" data-testid="sponsor-section">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-10">
              <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-4">
                A More Personal Way
              </div>
              <h2 className="font-display text-3xl sm:text-4xl text-[#F4F1EA] mb-4">Sponsor A Truth</h2>
              <div className="w-12 h-px bg-[#C9A055] mx-auto mb-6" />
              <p className="font-body text-[15px] text-[#B5B0A7] max-w-xl mx-auto leading-[1.7]">
                Choose an object — a hammer, a chair, a rope. If selected, you&rsquo;ll be
                credited in the Truth Registry as the inspiration behind the piece.
              </p>
              <div className="mt-5 inline-flex items-center gap-2 font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055]">
                <Sparkles size={12} /> $50 minimum
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {sponsorables.map((a) => {
                const selected = sponsorMode && sponsorArtwork?.id === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => {
                      setSponsorMode(true);
                      setSponsorArtwork(a);
                      if (Number(amount) < 50) setAmount(50);
                      document.getElementById("support-form")?.scrollIntoView({ behavior: "smooth" });
                    }}
                    data-testid={`sponsor-artwork-${a.slug || a.id}`}
                    className={`border bg-[#0a0a0a] aspect-[3/4] overflow-hidden relative group transition-all ${selected ? "border-[#C9A055] ring-1 ring-[#C9A055]" : "border-white/10 hover:border-[#C9A055]/50"}`}
                  >
                    {a.main_image && (
                      <img src={a.main_image} alt={a.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-left">
                      <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#C9A055] mb-1">
                        Truth #{String(a.collection_number || "—").padStart(2, "0")}
                      </div>
                      <div className="font-display text-sm text-[#F4F1EA] uppercase">{a.title}</div>
                    </div>
                    {selected && (
                      <div className="absolute top-2 right-2 bg-[#C9A055] text-black p-1 rounded-full">
                        <Check size={12} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {sponsorMode && sponsorArtwork && (
              <div className="mt-6 text-center font-body text-sm text-[#C9A055]" data-testid="sponsor-selected-banner">
                Sponsoring <span className="italic">{sponsorArtwork.title}</span> ·{" "}
                <button onClick={() => { setSponsorMode(false); setSponsorArtwork(null); }} className="underline text-[#8a857d] hover:text-[#F4F1EA]">
                  Cancel sponsor
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ====== FORM ====== */}
      <section id="support-form" className="bg-[#0a0a0a] py-16 sm:py-24" data-testid="support-form-section">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-4">
              {sponsorMode ? "Sponsor A Truth" : "Choose Your Support"}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#F4F1EA]">
              {sponsorMode ? `Sponsor: ${sponsorArtwork?.title}` : "Support Through Stripe"}
            </h2>
            <div className="w-12 h-px bg-[#C9A055] mx-auto mt-5" />
          </div>

          {/* Preset buttons */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-5">
            {PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => { setAmount(p); setCustomMode(false); }}
                data-testid={`preset-${p}`}
                className={`py-3 font-display border transition-all ${Number(amount) === p && !customMode ? "bg-[#C9A055] text-black border-[#C9A055]" : "bg-[#0a0a0a] text-[#F4F1EA] border-white/15 hover:border-[#C9A055]"}`}
              >
                ${p}
              </button>
            ))}
          </div>

          {/* Custom amount */}
          <div className="flex items-center gap-3 mb-6">
            <button
              type="button"
              onClick={() => { setCustomMode(true); setAmount(""); }}
              className={`font-ui text-[10px] tracking-[0.22em] uppercase px-3 py-2 border ${customMode ? "border-[#C9A055] text-[#C9A055]" : "border-white/15 text-[#8a857d] hover:text-[#F4F1EA]"}`}
              data-testid="custom-amount-toggle"
            >
              Custom Amount
            </button>
            {customMode && (
              <div className="flex-1 flex items-center border border-white/15 bg-[#050505] px-3">
                <span className="text-[#8a857d]">$</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="bg-transparent text-[#F4F1EA] font-display text-lg w-full px-2 py-2 outline-none"
                  data-testid="custom-amount-input"
                  autoFocus
                />
              </div>
            )}
          </div>

          {/* Tier indicator */}
          {currentTier && Number(amount) > 0 && (
            <div className="mb-7 text-center font-ui text-[11px] tracking-[0.22em] uppercase text-[#C9A055]" data-testid="tier-indicator">
              You unlock: {currentTier.name}
            </div>
          )}

          {/* Identity */}
          <div className="space-y-4 mb-6">
            <input
              data-testid="support-name"
              className="stc-input"
              placeholder="Your name (shown on Supporters Wall if opted in)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
            <input
              data-testid="support-email"
              type="email"
              className="stc-input"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {sponsorMode && (
              <input
                data-testid="sponsor-city"
                className="stc-input"
                placeholder="City, State (e.g. Nashville, TN) — shown beside your name in the Registry"
                value={sponsorCity}
                onChange={(e) => setSponsorCity(e.target.value)}
                maxLength={80}
              />
            )}
            <textarea
              data-testid="support-message"
              className="stc-input min-h-[90px]"
              placeholder="Optional message (we'll read every one)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={600}
            />
            <label className="flex items-center gap-2 font-body text-sm text-[#B5B0A7] cursor-pointer">
              <input
                type="checkbox"
                checked={showOnWall}
                onChange={(e) => setShowOnWall(e.target.checked)}
                data-testid="show-on-wall"
                className="accent-[#C9A055]"
              />
              Add my name to the public Supporters Wall
            </label>
          </div>

          <button
            type="button"
            onClick={submit}
            disabled={busy || !Number(amount)}
            data-testid="support-submit"
            className="btn-gold w-full py-4 text-base flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Lock size={14} />
            {busy ? "Opening secure checkout…" : `Support With $${Number(amount) || 0} via Stripe`}
          </button>
          <div className="text-center mt-3 font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d]">
            Secure card payment · powered by Stripe
          </div>

          {/* Venmo / Cash App */}
          <div className="mt-10 pt-10 border-t border-white/10">
            <div className="text-center font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-5">
              Prefer Venmo or Cash App?
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <a
                href="https://venmo.com/u/MickeyTrivett"
                target="_blank" rel="noopener noreferrer"
                data-testid="venmo-link"
                className="border border-white/15 bg-[#050505] p-5 hover:border-[#3D95CE] flex items-center justify-between"
              >
                <div>
                  <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#3D95CE]">Venmo</div>
                  <div className="font-display text-lg text-[#F4F1EA] mt-1">@MickeyTrivett</div>
                </div>
                <ExternalLink size={16} className="text-[#8a857d]" />
              </a>
              <a
                href="https://cash.app/$MickeyTrivett"
                target="_blank" rel="noopener noreferrer"
                data-testid="cashapp-link"
                className="border border-white/15 bg-[#050505] p-5 hover:border-[#00D54B] flex items-center justify-between"
              >
                <div>
                  <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#00D54B]">Cash App</div>
                  <div className="font-display text-lg text-[#F4F1EA] mt-1">$MickeyTrivett</div>
                </div>
                <ExternalLink size={16} className="text-[#8a857d]" />
              </a>
            </div>
            <p className="text-center mt-4 font-body text-xs text-[#8a857d]">
              Sending direct? Drop a note saying &ldquo;Truth Mission&rdquo; so I can add you to the wall.
            </p>
          </div>
        </div>
      </section>

      {/* ====== SUPPORTERS WALL ====== */}
      {supporters.length > 0 && (
        <section className="bg-[#050505] py-16 sm:py-20 border-y border-white/5" data-testid="supporters-wall-section">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-4">
              Supporters Wall
            </div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#F4F1EA] mb-4">
              Thank you to those who believe
            </h2>
            <div className="w-12 h-px bg-[#C9A055] mx-auto mb-10" />
            <div className="flex flex-wrap gap-2 justify-center">
              {supporters.map((s, i) => {
                const tone = TIER_META[s.tier_slug]?.color || "#C9A055";
                return (
                  <div
                    key={i}
                    data-testid={`wall-supporter-${i}`}
                    className="border bg-[#0a0a0a] px-3 py-1.5 font-body text-sm text-[#F4F1EA]"
                    style={{ borderColor: `${tone}40` }}
                    title={s.tier_name + (s.is_sponsor_a_truth ? " · Sponsored a Truth" : "")}
                  >
                    {s.name}
                    {s.is_sponsor_a_truth && (
                      <span className="ml-1.5 font-ui text-[9px] tracking-[0.22em] uppercase" style={{ color: tone }}>· sponsor</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ====== MICKEY'S MESSAGE ====== */}
      <section className="bg-[#0a0a0a] py-16 sm:py-24" data-testid="mickey-message">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-4">
              From The Founder
            </div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#F4F1EA]">Why I Created Simple Truths</h2>
            <div className="w-12 h-px bg-[#C9A055] mx-auto mt-6" />
          </div>
          <div className="space-y-5 font-body text-[16px] leading-[1.85] text-[#B5B0A7] text-center">
            <p>I don&rsquo;t know if artwork can change the world.</p>
            <p>But I know a single idea can change a day.</p>
            <p>And sometimes a day can change a life.</p>
            <p className="text-[#F4F1EA] pt-3">
              Thank you for believing in this mission and helping me continue creating.
            </p>
          </div>
          <div className="text-center mt-10">
            <div className="font-display text-lg text-[#C9A055] italic mb-2">
              Live Life. Do Good. Be Happy.
            </div>
            <div className="font-display text-base text-[#F4F1EA]">— Mickey</div>
          </div>
        </div>
      </section>

      {/* ====== THANKS MODAL (post-checkout) ====== */}
      {thanksOpen && thanksData && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="thanks-modal">
          <div className="bg-[#0a0a0a] border border-[#C9A055]/60 max-w-md w-full p-8 text-center">
            <Heart size={36} className="mx-auto text-[#C9A055] mb-4" />
            <div className="font-display text-2xl text-[#F4F1EA] mb-2">Thank you, {thanksData.name}.</div>
            <div className="font-body text-sm text-[#B5B0A7] mb-5">
              You&rsquo;ve supported the mission at the <span className="text-[#C9A055]">{thanksData.tier_name}</span> level.
            </div>
            {thanksData.is_sponsor_a_truth && thanksData.sponsor_artwork_title && (
              <div className="border border-[#C9A055]/30 bg-[#050505] p-4 mb-5">
                <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#C9A055] mb-1">You Sponsored</div>
                <div className="font-display text-xl text-[#F4F1EA]">{thanksData.sponsor_artwork_title}</div>
                <div className="font-body text-xs text-[#8a857d] mt-2">
                  You&rsquo;ll be credited in the Truth Registry as the inspiration behind this piece.
                </div>
              </div>
            )}
            <button
              onClick={() => { setThanksOpen(false); window.history.replaceState({}, "", "/support"); }}
              className="btn-gold w-full"
              data-testid="thanks-close"
            >
              Continue
            </button>
            <Link to="/registry" className="block mt-3 font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] hover:text-[#C9A055]">
              See the Truth Registry →
            </Link>
          </div>
        </div>
      )}
    </Layout>
  );
}
